<template>
    <tailwind-layout title="Rent Payments: Edit Property">
        <template #header>
            <div class="mb-4 px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center">
                <h1 class="font-semibold text-2xl leading-tight">Rent Payments: Edit Property</h1>
                <div class="flex items-center space-x-4">
                    <inertia-link class="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10" :href="route('rented-properties.index')">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>
        </template>
        <div class="overflow-hidden shadow-xl rounded-lg p-6 bg-white">
            <form @submit.prevent="update" class="space-y-8 divide-y divide-gray-200">
                <div class="space-y-8 divide-y divide-gray-200">
                    <div>
                        <div>
                            <h3 class="text-lg leading-6 font-medium text-gray-900">
                                Property Details
                            </h3>
                        </div>
                        <div v-if="!property.bank_account" class="rounded-md bg-yellow-50 p-4 mt-2">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <font-awesome-icon icon="fa-info-circle" class="h-5 w-5 text-yellow-400" aria-hidden="true" />
                                </div>
                                <div class="ml-3 text-sm text-yellow-700">
                                    <p>
                                        If you've not setup your bank account please visit <span class="font-bold">Rent Payments > Bank Accounts</span> Menu or <inertia-link :href="route('user-bank-accounts.index')" class="text-blue-600"> Click here.</inertia-link>
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                            <div class="sm:col-span-2">
                                <label for="owner_name" v-bind:class="(form.errors.owner_name)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Owner/Landlord Name
                                </label>
                                <div class="mt-1">
                                    <input id="owner_name" v-model="form.owner_name" type="text" v-bind:class="(form.errors.owner_name)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-lookup" class="block text-sm font-medium text-gray-700">
                                    Address Lookup
                                </label>
                                <div class="mt-1">
                                    <div class="mt-1 relative rounded-md">
                                        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <font-awesome-icon :icon="faMapMarkerAlt" class="text-gray-400" />
                                        </div>
                                        <GMapAutocomplete
                                            id="address_lookup"
                                            ref="autocompleteRef"
                                            placeholder="Start typing address..."
                                            @place_changed="initAutocomplete"
                                            :options="{
                                                componentRestrictions: { country: ['US', 'VI', 'AS', 'GU', 'PR', 'MP'] }
                                            }"
                                            v-bind:class="'py-0.5 h-10 px-1 mt-1 shadow-sm h-8 focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border border-gray-300 rounded-md pl-10'"
                                            data-demo-target="address_lookup"
                                        >
                                        </GMapAutocomplete>
                                    </div>
                                    <p class="text-xs text-gray-500">Please include postal code for accuracy.</p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-1" v-bind:class="(form.errors.address_1)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 1 <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="address_1" v-model="form.address_1" readonly :error="form.errors.address_1" type="text" v-bind:class="(form.errors.address_1)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md bg-gray-100':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-100  bg-gray-100'">
                                    <p v-text="form.errors.address_1" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="address-2" v-bind:class="(form.errors.address_2)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Address 2
                                </label>
                                <div class="mt-1">
                                    <input id="address-2" v-model="form.address_2" type="text" v-bind:class="(form.errors.address_2)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md'">
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="city" v-bind:class="(form.errors.city)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    City <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="city" v-model="form.city" readonly type="text" v-bind:class="(form.errors.city)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md  bg-gray-100'">
                                    <p v-text="form.errors.city" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="state" v-bind:class="(form.errors.state)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    State <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input v-model="form.state" type="hidden">
                                    <select id="state" disabled v-bind:value="form.state" v-model="form.state" :error="form.errors.state" v-bind:class="(form.errors.state)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-gray-100'">
                                        <option v-for="(state, index) in states" :value="index">{{ state }}</option>
                                    </select>
                                    <p v-text="form.errors.state" class="text-red-500 text-sm"></p>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label for="zip_code" v-bind:class="(form.errors.zip_code)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Zip Code <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <input id="zip_code" v-model="form.zip_code" readonly type="text" v-bind:class="(form.errors.zip_code)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md bg-gray-100'">
                                    <p v-text="form.errors.zip_code" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="rent" v-bind:class="(form.errors.rent)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Monthly Rent <span class="text-red-700">*</span>
                                </label>

                                <div class="mt-1 relative rounded-md">
                                    <div class="absolute left-0 pl-3 flex items-center pointer-events-none" v-bind:class="(form.errors.rent)?'top-3':'inset-y-0'">
                                        <font-awesome-icon :icon="faDollarSign" class="text-gray-400" />
                                    </div>
                                    <input id="rent" v-model="form.rent" type="number" step="0.01" data-demo-target="rent-amount" v-bind:class="(form.errors.rent)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md pl-10':'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md pl-10'">
                                    <p v-text="form.errors.rent" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <label for="user_bank_account_id" v-bind:class="(form.errors.user_bank_account_id)?'block text-sm font-medium text-red-500':'block text-sm font-medium text-gray-700'">
                                    Link a Bank Account <span class="text-red-700">*</span>
                                </label>
                                <div class="mt-1">
                                    <select id="user_bank_account_id" v-model="form.user_bank_account_id" :error="form.errors.user_bank_account_id" data-demo-target="bank-account" v-bind:class="(form.errors.user_bank_account_id)?'shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-red-300 rounded-md':'mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'">
                                        <option v-for="(account, index) in accounts" :value="account.id">**** **** **** {{account.account_last_4}} ({{account.bank_name}})</option>
                                    </select>
                                    <p v-text="form.errors.user_bank_account_id" class="text-red-500 text-sm"></p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="pt-5">
                    <div class="flex justify-end">
                        <inertia-link :href="route('rented-properties.index')" class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">Cancel</inertia-link>
                        <form-button data-demo-target="save-updates" :form="form" type="submit" class="ml-3">
                            Update
                        </form-button>
                    </div>
                </div>
            </form>
        </div>
    
    <!-- Demo Widget -->
    <DemoWidget
        :page-identifier="'rented-properties.edit'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
    </tailwind-layout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import FormButton from '@/Shared/FormButton.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import RadioGroup from "@/Shared/RadioGroup.vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronLeft, faDollarSign, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';
import DemoWidget from '@/Components/DemoWidget.vue'
import { getDemoSteps } from '@/Config/demoSteps.js'
export default {
    props: {
        states: Object,
        property: Object,
        accounts: Object,
    },
    components: {
        TailwindLayout,
        FormButton,
        SelectInput,
        RadioGroup,
        FontAwesomeIcon,
        DemoWidget
    },
    remember: 'form',
    data() {
        return {
            faChevronLeft,
            faDollarSign,
            faMapMarkerAlt,
            form: this.$inertia.form({
                owner_name: this.property.owner_name,
                address_1: this.property.address_1,
                address_2: this.property.address_2,
                city: this.property.city,
                state: this.property.state,
                county: this.property.county,
                zip_code: this.property.zip_code,
                rent: this.property.rent,
                user_bank_account_id: this.property.bank_account?.id,
            }),
            demoSteps: getDemoSteps('rented-properties.edit')
        }
    },
    methods: {
        update() {
            this.form.put(this.route('rented-properties.update', this.property.id))
        },
        initAutocomplete(place){
            // RAC-9990 — see Pages/V2/Properties/AddProperty.vue for the full story.
            // Surface inline error via form.errors.address_1 (existing template binding).
            if (!place?.address_components) {
                this.form.errors.address_1 = "We couldn't read a full address from that result. Please pick a more specific result from the dropdown — make sure it's a street address, not a business or area.";
                return;
            }
            this.form.errors.address_1 = null;

            let street = null;
            let route = null;
            this.form.city = null;

            place.address_components.forEach(component => {
                const componentType = component.types[0];
                switch (componentType) {
                    case "street_number": {
                        street = `${component.long_name}`;
                        break;
                    }
                    case "route": {
                        route = ' '+component.short_name;
                        break;
                    }
                    case "postal_code": {
                        this.form.zip_code = `${component.long_name}`;
                        break;
                    }
                    case "locality":
                        this.form.city = component.long_name;
                        break;
                    case "neighborhood":
                        if (this.form.city === null) {
                            this.form.city = component.long_name;
                        }
                        break;
                    case "administrative_area_level_2":
                        this.form.county = component.long_name;
                        break;
                    case "administrative_area_level_1": {
                        this.form.state = `${component.short_name}`;
                        break;
                    }
                    case "country": {
                        if(['VI', 'AS', 'GU', 'PR', 'MP'].includes(component.short_name)) {
                            this.form.state = `${component.short_name}`;
                        }
                        break;
                    }
                }
            });
            if(street === null || route === null) {
                this.form.address_1 = null;
                this.form.errors.address_1 = 'Address 1 not found. Enter the full address in Google search.';
            } else {
                this.form.address_1 = `${street} ${route}`
                this.form.errors.address_1 = null;
            }
            this.$refs.autocompleteRef.$refs.input.value = null;
        },
    }
}
</script>
